import { Emitter, EventCallback } from "./event";

type CallbackParams = { channel: Channel };

type TEvents = {
  appear: CallbackParams;
  away: CallbackParams;
  connected: CallbackParams;
  disconnected: CallbackParams;
  initialized: CallbackParams;
  install: CallbackParams;
  rejected: CallbackParams;
  uninstall: CallbackParams;
  update: CallbackParams;
  received: CallbackParams & { data: Record<string, unknown> };
};

export default class Channel {
  private _sub: ActionCable.Subscription;
  private _emitter = Emitter<TEvents>();

  constructor(
    cable: ActionCable.Consumer,
    channel: string,
    parameters: Record<string, string>,
  ) {
    const params = {
      channel: channel,
      ...parameters,
    };

    this._sub = cable.subscriptions.create(params, {
      appear: () => this._emitter.emit("appear", { channel: this }),
      away: () => this._emitter.emit("away", { channel: this }),
      connected: () => this._emitter.emit("connected", { channel: this }),
      disconnected: () => this._emitter.emit("disconnected", { channel: this }),
      initialized: () => this._emitter.emit("initialized", { channel: this }),
      install: () => this._emitter.emit("install", { channel: this }),
      rejected: () => this._emitter.emit("rejected", { channel: this }),
      uninstall: () => this._emitter.emit("uninstall", { channel: this }),
      update: () => this._emitter.emit("update", { channel: this }),
      received: (data: unknown) => {
        const typedData = data;
        if (typeof typedData !== "object" || typedData === null) {
          throw new Error("Received data is not an object");
        }

        this._emitter.emit("received", {
          channel: this,
          data: typedData as Record<string, unknown>,
        });
      },
    });
  }

  perform(action: string, data?: Record<string, unknown>): void {
    this._sub.perform(action, data);
  }

  on(
    event: keyof TEvents,
    callback: EventCallback<TEvents[keyof TEvents]>,
  ): void {
    this._emitter.on(event, callback);
  }

  off(
    event: keyof TEvents,
    callback: EventCallback<TEvents[keyof TEvents]>,
  ): void {
    this._emitter.off(event, callback);
  }
}
